export type AdvanceTrigger =
  | { type: "next-button" }
  | { type: "click-anchor" }
  | { type: "send-message" }
  | { type: "agent-applied" }
  | { type: "route-change"; pathSuffix: string }
  | { type: "custom-event"; eventName: string };

export type StepSideEffect =
  | { type: "set-chat-width"; width: number | "max" | "closed" }
  | { type: "set-chat-input"; value: string }
  | { type: "set-chat-mode"; mode: "ask" | "agent" }
  | { type: "set-chat-tagged-agents"; agentKeys: string[] }
  | { type: "clear-chat-tagged-agents" }
  | { type: "enable-advanced-pricing" }
  | { type: "enable-registry" };

export type WalkthroughStep = {
  id: string;
  /** CSS selector for the pulsing dot anchor. If undefined, bubble centers in viewport. */
  anchor?: string;
  /** CSS selector for the cutout/highlight rectangle. Defaults to `anchor`. */
  highlightAnchor?: string;
  /** If set, use this selector to detect clicks for click-anchor advance (instead of anchor). */
  clickAnchor?: string;
  title?: string;
  body: string;
  advance: AdvanceTrigger;
  /** Fired when the step is entered. */
  onEnter?: StepSideEffect[];
  /** Where the bubble should appear relative to the anchor. */
  placement?: "top" | "bottom" | "left" | "right" | "center";
  /** Hide the pulsing dot for this step. */
  hideDot?: boolean;
  /** Pixel offset for dot X position (after natural placement). */
  dotOffsetX?: number;
  /** Pixel offset for dot Y position (after natural placement). */
  dotOffsetY?: number;
  /** Border-radius for the spotlight highlight (creates pill/rounded shape). */
  highlightRadius?: number | string;
  /** If true, skip cutting a hole in the overlay — no spotlight effect, just dim. */
  noHighlight?: boolean;
  /** If set for send-message steps, the send button pulses blue as the CTA. */
  pulseSendButton?: boolean;
  /** If true, hide the entire background overlay (dim/spotlight) for this step. */
  hideOverlay?: boolean;
  /** Optional override for the bubble anchor. If not set, bubble anchors to `anchor`. */
  bubbleAnchor?: string;
  /** Optional override for the spotlight padding. Defaults to 8. */
  highlightPadding?: number;
  /** Pixel offset for bubble X position. */
  bubbleOffsetX?: number;
  /** Pixel offset for bubble Y position. */
  bubbleOffsetY?: number;
  /** If true, show this step as a centered Modal with an "End Walkthrough" button. */
  isCompletionModal?: boolean;
  /** If true, render a "Next" button inside the bubble that advances the step. */
  showNextButton?: boolean;
  /** Override the delay between click-anchor click and advancing (default 150ms). */
  clickAdvanceDelayMs?: number;
};

export const WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    id: "welcome",
    body:
      "Welcome to your first parking lot on Parallel Operator. Let's take a walkthrough! Tip: press Enter to skip a step.",
    advance: { type: "next-button" },
    placement: "center",
    hideDot: true,
  },
  {
    id: "analytics",
    // Dot is centered under the occupancy icon, shifted down a bit
    anchor: '[data-walkthrough="analytics-selector"] button.occupancy',
    // Highlight the whole analytics selector bar
    highlightAnchor: '[data-walkthrough="analytics-selector"]',
    highlightRadius: 1000,
    // Allow clicking any analytics button to advance
    clickAnchor: '[data-walkthrough="analytics-selector"] button',
    body:
      "There are many analytics to view: revenue, occupancy, segmentation, and more. Try clicking any page.",
    advance: { type: "click-anchor" },
    placement: "bottom",
    dotOffsetY: 10,
    bubbleOffsetX: -16,
    bubbleOffsetY: 10,
  },
  {
    id: "timeframe",
    // Dot under the Week button, shifted down
    anchor: '[data-walkthrough="timeframe-selector"] button:nth-of-type(2)',
    // Highlight the full timeframe selector
    highlightAnchor: '[data-walkthrough="timeframe-selector"]',
    highlightRadius: 1000,
    // Allow any timeframe button click to advance
    clickAnchor: '[data-walkthrough="timeframe-selector"] button',
    body: "View any analytic by day, week, month, or year.",
    advance: { type: "click-anchor" },
    placement: "bottom",
    dotOffsetY: 10,
    bubbleOffsetX: -16,
    bubbleOffsetY: 10,
  },
  {
    id: "download",
    anchor: '[data-walkthrough="download-data"]',
    // No spotlight highlight — just the dot and bubble
    noHighlight: true,
    body:
      "Download all your data for the selected time period as a report with this button.",
    advance: { type: "click-anchor" },
    placement: "bottom",
    hideOverlay: true,
    bubbleOffsetX: -16,
    bubbleOffsetY: 6,
  },
  {
    id: "ai-ask",
    // Dot on send button, but bubble anchored to the panel's left edge
    anchor: ".ai-chat-send-button",
    bubbleAnchor: ".ai-chat",
    highlightAnchor: ".ai-chat",
    highlightPadding: 0,
    body:
      "Ask AI anything about your parking lot. Edit the message if you'd like, then press send.",
    advance: { type: "send-message" },
    placement: "left",
    // Shift vertically to be near bottom (bottom-aligned + raised 100px)
    bubbleOffsetY: 400,
    // No dot — the send button itself pulses instead
    hideDot: true,
    pulseSendButton: true,
    onEnter: [
      { type: "set-chat-width", width: "max" },
      { type: "set-chat-mode", mode: "ask" },
      { type: "clear-chat-tagged-agents" },
      { type: "set-chat-input", value: "What was my busiest time this year?" },
    ],
  },
  {
    id: "ai-agent",
    // Dot on send button, but bubble anchored to the panel's left edge
    anchor: ".ai-chat-send-button",
    bubbleAnchor: ".ai-chat",
    highlightAnchor: ".ai-chat",
    highlightPadding: 0,
    body:
      "Switch to Agent mode so Parallel can run actions for you. Tag specific agents like pricing or settings using the @ symbol.",
    advance: { type: "send-message" },
    placement: "left",
    bubbleOffsetY: 400,
    hideDot: true,
    pulseSendButton: true,
    onEnter: [
      { type: "set-chat-mode", mode: "agent" },
      { type: "clear-chat-tagged-agents" },
      { type: "set-chat-tagged-agents", agentKeys: ["lot_pricing_agent", "lot_settings_agent"] },
      { type: "set-chat-input", value: "Change hourly rate to 6 dollars an hour" },
    ],
  },
  {
    id: "ai-approve",
    anchor: ".ai-chat-agent-confirm",
    highlightAnchor: ".ai-chat",
    highlightPadding: 0,
    body: "Approve actions to apply them to your lot.",
    advance: { type: "agent-applied" },
    placement: "left",
    bubbleOffsetX: -14,
    dotOffsetX: -14,
    dotOffsetY: 0,
    showNextButton: true,
  },
  {
    id: "settings",
    anchor: '[data-nav-path$="/settings"]',
    highlightAnchor: '[data-nav-path$="/settings"]',
    highlightRadius: 8,
    bubbleOffsetY: 22,
    bubbleOffsetX: -8,
    dotOffsetX: -10,
    body:
      "You can view changes right on the page. Let's open Settings to see what changed.",
    advance: { type: "route-change", pathSuffix: "/settings" },
    placement: "right",
    onEnter: [{ type: "set-chat-width", width: "closed" }],
  },
  {
    id: "settings-edit",
    anchor: '[data-walkthrough="settings-save"]',
    highlightAnchor: '[data-walkthrough="settings-page"]',
    highlightRadius: 12,
    highlightPadding: 16,
    bubbleOffsetX: -16,
    body:
      "You can edit settings manually here. When you're done, hit save.",
    advance: { type: "next-button" },
    placement: "bottom",
    showNextButton: true,
  },
  {
    id: "nav-advanced",
    anchor: '[data-nav-path$="/advanced"]',
    highlightAnchor: '[data-nav-path$="/advanced"]',
    highlightRadius: 8,
    bubbleOffsetY: 32,
    bubbleOffsetX: -8,
    dotOffsetX: -10,
    body: "Now click Advanced in the sidebar.",
    advance: { type: "route-change", pathSuffix: "/advanced" },
    placement: "right",
  },
  {
    id: "advanced-monday",
    anchor: '[data-walkthrough="advanced-first-block"]',
    highlightAnchor: '[data-walkthrough="advanced-monday-column"]',
    highlightRadius: 12,
    highlightPadding: 4,
    body:
      "Advanced settings let you set custom times for specific days. Change this block's mode to Set Time to customize a specific period.",
    advance: { type: "custom-event", eventName: "walkthrough-advanced-set-time" },
    placement: "right",
    showNextButton: true,
    onEnter: [{ type: "enable-advanced-pricing" }],
  },
  {
    id: "advanced-default-block",
    anchor: '[data-walkthrough="advanced-default-block"]',
    highlightAnchor: '[data-walkthrough="advanced-default-block"]',
    highlightRadius: 12,
    highlightPadding: 4,
    bubbleOffsetY: -30,
    body:
      "A Default block is automatically generated for the remaining time, billing any unspecified hours at the default rate. You can change this block too, into a second time block.",
    advance: { type: "next-button" },
    placement: "right",
    showNextButton: true,
  },
  {
    id: "nav-registry",
    anchor: '[data-nav-path$="/registry"]',
    highlightAnchor: '[data-nav-path$="/registry"]',
    highlightRadius: 8,
    bubbleOffsetY: 32,
    bubbleOffsetX: -8,
    dotOffsetX: -10,
    body: "Next, click Registry in the sidebar.",
    advance: { type: "route-change", pathSuffix: "/registry" },
    placement: "right",
  },
  {
    id: "registry-info",
    anchor: ".placeholder-row",
    highlightAnchor: ".placeholder-row",
    highlightRadius: 8,
    bubbleOffsetX: -16,
    body:
      "This is the Registry page. List all your permit holders here, they won't be billed, charged, or ticketed by the automated systems. If you already have a registry, you can upload it and we'll add it automatically.",
    advance: { type: "next-button" },
    placement: "bottom",
    showNextButton: true,
    onEnter: [{ type: "enable-registry" }],
  },
  {
    id: "nav-occupants",
    anchor: '[data-nav-path$="/occupants"]',
    highlightAnchor: '[data-nav-path$="/occupants"]',
    highlightRadius: 8,
    bubbleOffsetY: 32,
    bubbleOffsetX: -8,
    dotOffsetX: -10,
    body: "Now click Occupants in the sidebar.",
    advance: { type: "route-change", pathSuffix: "/occupants" },
    placement: "right",
  },
  {
    id: "occupants-info",
    body:
      "This page shows occupants currently in your lot. You can search, filter, or sort.",
    advance: { type: "next-button" },
    placement: "center",
    hideDot: true,
    hideOverlay: true,
    noHighlight: true,
    showNextButton: true,
  },
  {
    id: "nav-violators",
    anchor: '[data-nav-path$="/violators"]',
    highlightAnchor: '[data-nav-path$="/violators"]',
    highlightRadius: 8,
    bubbleOffsetY: 32,
    bubbleOffsetX: -8,
    dotOffsetX: -10,
    body: "Now click Violators in the sidebar.",
    advance: { type: "route-change", pathSuffix: "/violators" },
    placement: "right",
  },
  {
    id: "violator-row",
    anchor: '[data-walkthrough="first-violator-row"]',
    highlightAnchor: '[data-walkthrough="first-violator-row"]',
    highlightRadius: 8,
    bubbleOffsetX: -16,
    clickAnchor: '[data-walkthrough="first-violator-row"]',
    body:
      "The Violators page shows previously administered tickets and notes. You can generate new violations or remove them. Click to expand.",
    advance: { type: "click-anchor" },
    placement: "bottom",
    clickAdvanceDelayMs: 5000,
  },
  {
    id: "complete",
    body:
      "And that's just a few of our features! We have plenty of tutorial videos, but even simpler, just ask AI if you have a question.",
    advance: { type: "next-button" },
    placement: "center",
    hideDot: true,
    isCompletionModal: true,
  },
];
